// pages/Projects.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  Tooltip,
  DialogContentText,
  TableSortLabel,
  TablePagination,
  Autocomplete,
  Checkbox,
  Chip,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import { Navigate } from "react-router-dom";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  writeBatch,   // ✅ ADD THIS
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";
import { logActivity } from "../utils/activityLogger";

import ListSubheader from "@mui/material/ListSubheader";


const statusOptions = ["In Progress", "Completed", "Pending"];
const priorityOptions = ["High", "Medium", "Low"];
// Default project categories; users can add more from the dialog (free text)
const defaultCategoryOptions = ["Audit & Assurance", "Tax Audit", "Certification"];

function Projects() {
  const { companyId, user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;

  const [projects, setProjects] = useState([]);
  const [entities, setEntities] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(defaultCategoryOptions);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterEntity, setFilterEntity] = useState("");
  const [filterEntities, setFilterEntities] = useState([]); // array of entity names

  // Entity Type filter state + helpers
  const [entityMap, setEntityMap] = useState({}); // { [entityName]: type }
  const [entityTypeOptions, setEntityTypeOptions] = useState([]); // e.g. ["Client","Vendor","Partner"]
  const [filterEntityTypes, setFilterEntityTypes] = useState([]); // selected types

  const [originalProject, setOriginalProject] = useState(null);

const [entityQuery, setEntityQuery] = useState("");
const [teamLeadQuery, setTeamLeadQuery] = useState("");

const [managedByQuery, setManagedByQuery] = useState("");


  const ENTITY_TYPE_ALL = {
    id: "__ALL__",
    label: `Select all (${entityTypeOptions.length})`,
  };
  const entityTypeOpts = [
    ENTITY_TYPE_ALL,
    ...entityTypeOptions.map((t) => ({ id: t, label: t })),
  ];
  const allEntityTypesSelected =
    entityTypeOptions.length > 0 &&
    filterEntityTypes.length === entityTypeOptions.length;

  // "Select all" options (labels update automatically with counts)
  const ENTITY_ALL = { id: "__ALL__", label: `Select all (${entities.length})` };
  const STATUS_ALL = {
    id: "__ALL__",
    label: `Select all (${statusOptions.length})`,
  };
  const PRIORITY_ALL = {
    id: "__ALL__",
    label: `Select all (${priorityOptions.length})`,
  };

  // Prepped option lists like LoginActivity.js
  const entityOptions = [ENTITY_ALL, ...entities.map((e) => ({ id: e, label: e }))];
  const statusOpts = [STATUS_ALL, ...statusOptions.map((s) => ({ id: s, label: s }))];
  const priorityOpts = [
    PRIORITY_ALL,
    ...priorityOptions.map((p) => ({ id: p, label: p })),
  ];

  // Convenience flags for indeterminate checkbox state
  const allEntitiesSelected =
    entities.length > 0 && filterEntities.length === entities.length;
  const allStatusSelected =
    filterStatus.length === statusOptions.length && statusOptions.length > 0;
  const allPrioritySelected =
    filterPriority.length === priorityOptions.length &&
    priorityOptions.length > 0;

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`; // dd-mm-yyyy
  };

  // sorting + pagination state
  const [sortField, setSortField] = useState("name"); // default sort by Project Name
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Managers to pick from (users in this company with role Manager/Admin)
  const [managers, setManagers] = useState([]);

  // NEW: All users list for Team Leader picker
  const [usersInCompany, setUsersInCompany] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentProject, setCurrentProject] = useState({
    id: null,
    name: "",
    category: "",       // NEW
    entity: "",
    status: "",
    priority: "",
    dueDate: "",
    createdBy: "",
    createdOn: "", // ISO
    modifiedOn: "", // ISO
    managerId: "",
    managerName: "",
    // NEW: team lead fields
    teamLeadId: "",
    teamLeadName: "",
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

const sortedEntityNames = useMemo(() => {
  const names = (entities || [])
    .map((e) => (e || "").trim())
    .filter(Boolean);

  return Array.from(new Set(names)).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}, [entities]);

const filteredEntityNames = useMemo(() => {
  const q = entityQuery.trim().toLowerCase();
  if (!q) return sortedEntityNames;
  return sortedEntityNames.filter((n) => n.toLowerCase().includes(q));
}, [sortedEntityNames, entityQuery]);



const sortedManagedByUsers = useMemo(() => {
  return (usersInCompany || [])
    .slice()
    .sort((a, b) =>
      (a?.name || "").trim().localeCompare((b?.name || "").trim(), undefined, {
        sensitivity: "base",
      })
    );
}, [usersInCompany]);

const filteredManagedByUsers = useMemo(() => {
  const q = managedByQuery.trim().toLowerCase();
  if (!q) return sortedManagedByUsers;
  return sortedManagedByUsers.filter((u) =>
    (u?.name || "").toLowerCase().includes(q)
  );
}, [sortedManagedByUsers, managedByQuery]);



const sortedTeamLeadUsers = useMemo(() => {
  return (usersInCompany || [])
    .slice()
    .sort((a, b) =>
      (a?.name || "").trim().localeCompare((b?.name || "").trim(), undefined, {
        sensitivity: "base",
      })
    );
}, [usersInCompany]);

const filteredTeamLeadUsers = useMemo(() => {
  const q = teamLeadQuery.trim().toLowerCase();
  if (!q) return sortedTeamLeadUsers;
  return sortedTeamLeadUsers.filter((u) =>
    (u?.name || "").toLowerCase().includes(q)
  );
}, [sortedTeamLeadUsers, teamLeadQuery]);



  // Project Name filter
  const [projectOptions, setProjectOptions] = useState([]); // unique names
  const [filterProjects, setFilterProjects] = useState([]); // selected names
 // Controlled input for the Project Name filter (prevents resets)
 const [projectInputValue, setProjectInputValue] = useState("");
 // Mirror in a ref so filterOptions/renderOption/onChange share the *same* live query
 const projectInputRef = useRef("");

  const PROJECT_ALL = {
    id: "__ALL__",
    label: `Select all (${projectOptions.length})`,
  };
  const projectFilter = createFilterOptions();
  const projectOpts = [
    PROJECT_ALL,
    ...projectOptions.map((n) => ({ id: n, label: n })),
  ];
  const allProjectsSelected =
    projectOptions.length > 0 &&
    filterProjects.length === projectOptions.length;

  const onRequestDeleteProject = (project) => {
    setPendingDelete(project);
    setConfirmOpen(true);
  };

  const onConfirmDeleteProject = async () => {
    try {
      if (pendingDelete?.id) {
        await handleDeleteProject(pendingDelete.id);
      }
    } finally {
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  // Fetch entities for filter & Add/Edit dialog
  useEffect(() => {
    const fetchEntities = async () => {
      if (!companyId) return;
      try {
        const entityQuery = query(
          collection(db, "entities"),
          where("companyId", "==", companyId)
        );
        const querySnapshot = await getDocs(entityQuery);
        const docs = querySnapshot.docs.map((d) => d.data());

        const entityList = docs.map((d) => d.name);
        setEntities(entityList);

        // name -> type map
        const map = {};
        docs.forEach((d) => {
          if (d?.name) map[d.name] = d?.type || "";
        });
        setEntityMap(map);

        // unique non-empty types
        const types = Array.from(
          new Set(docs.map((d) => d?.type).filter(Boolean))
        );
        setEntityTypeOptions(types);
      } catch (error) {
        console.error("Failed to fetch entities:", error.message);
      }
    };
    fetchEntities();
  }, [companyId]);

  // Load Manager/Admin users AND everyone for Team Leader picker
 useEffect(() => {
  const fetchUsers = async () => {
    if (!companyId) return;

    try {
      const q = query(collection(db, "users"), where("companyId", "==", companyId));
      const snap = await getDocs(q);

      // Normalize each user to have { uid, name, role }
      const normalized = snap.docs.map((d) => {
        const u = d.data();
        const uid = u.uid || d.id;                     // << KEY: use auth uid if present, else the doc id
        const name = u.name || u.email || "Unnamed";
        const role = u.role || "Member";
        return { uid, name, role };
      });

      // For "Project Managed By"
      const mgrs = normalized.filter((u) => u.role === "Manager" || u.role === "Admin");
      setManagers(mgrs);

      // For "Team Leader"
      setUsersInCompany(normalized);
    } catch (e) {
      console.error("Failed to fetch company users:", e);
      setManagers([]);
      setUsersInCompany([]);
    }
  };

  fetchUsers();
}, [companyId]);

  // Default Entity filter = all entities once they’re loaded
  useEffect(() => {
    if (entities.length) {
      setFilterEntities(entities);
    }
  }, [entities]);

  // Fetch projects
  const fetchProjects = async () => {
    if (!companyId) return;
    try {
      const projectQuery = query(
        collection(db, "projects"),
        where("companyId", "==", companyId)
      );
      const querySnapshot = await getDocs(projectQuery);
      const projectList = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      let scoped = projectList;
      if (user?.role === "Manager") {
        scoped = projectList.filter((p) => p.managerId === user?.uid);
      }
      setProjects(scoped);

      setProjectOptions(
        Array.from(new Set(projectList.map((p) => p.name))).sort()
      );

    // NEW: refresh the dropdown options from all projects
     const cats = Array.from(
       new Set([
         ...defaultCategoryOptions,
         ...projectList.map((p) => p.category).filter(Boolean),
       ])
     ).sort((a, b) => a.localeCompare(b));
     setCategoryOptions(cats);

    } catch (error) {
      console.error("Error fetching projects:", error.message);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [companyId]);

  const handleOpenDialog = (project = null) => {
    setCurrentProject(
      project
        ? { ...project }
        : {
            id: null,
            name: "",
            category: "",
            entity: "",
            status: "",
            priority: "",
            dueDate: "",
            createdBy: "",
            // default manager for managers
            managerId: user?.role === "Manager" ? user?.uid : "",
            managerName: user?.role === "Manager" ? user?.name || "" : "",
            // team lead empty by default
            teamLeadId: "",
            teamLeadName: "",
          }
    );
    setOriginalProject(project ? { ...project } : null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentProject({
      id: null,
      name: "",
      category: "",
      entity: "",
      status: "",
      priority: "",
      dueDate: "",
      createdBy: "",
      managerId: user?.role === "Manager" ? user?.uid : "",
      managerName: user?.role === "Manager" ? user?.name || "" : "",
      teamLeadId: "",
      teamLeadName: "",
    });
    setOriginalProject(null);
  };

  const handleSaveProject = async () => {
    if (
      !currentProject.name ||
      !currentProject.entity ||
      !currentProject.status ||
      !currentProject.priority ||
      !currentProject.dueDate
    ) {
      alert("Please fill all fields.");
      return;
    }

    try {
      if (currentProject.id) {

   // Build field-level diffs
   const before = originalProject || {};
   const after = currentProject;
   const fields = [
     "name","category","entity","status","priority",
     "dueDate","managerName","teamLeadName"
   ];
   const changes = [];
   fields.forEach((f) => {
     const a = (before?.[f] ?? "") || "";
     const b = (after?.[f] ?? "") || "";
     if (a !== b) changes.push(`${f}: "${a || "—"}" → "${b || "—"}"`);
   });


        const projectRef = doc(db, "projects", currentProject.id);
        await updateDoc(projectRef, {
          name: currentProject.name,
          category: currentProject.category || "",
          entity: currentProject.entity,
          status: currentProject.status,
          priority: currentProject.priority,
          dueDate: currentProject.dueDate,
          companyId,
          modifiedOn: new Date().toISOString(), // keep ISO
          managerId: currentProject.managerId || user?.uid || "",
          managerName: currentProject.managerName || user?.name || "",
          // NEW: persist team lead on update
          teamLeadId: currentProject.teamLeadId || "",
          teamLeadName: currentProject.teamLeadName || "",
        });

        await logActivity({
          userId: user?.uid,
          userName: user?.name || "Unknown",
          companyId,
          action: "UPDATE",
          module: "Project",
          details:
         `Updated project "${currentProject.name}". ` +
         (changes.length ? `Changes: ${changes.join("; ")}` : "No visible field changes."),
        });

        await fetchProjects();
      } else {
        await addDoc(collection(db, "projects"), {
          name: currentProject.name,
          category: currentProject.category || "",
          entity: currentProject.entity,
          status: currentProject.status,
          priority: currentProject.priority,
          dueDate: currentProject.dueDate,
          companyId,
          createdBy: user?.name || "Unknown",
          createdOn: new Date().toISOString(), // keep ISO
          managerId: currentProject.managerId || user?.uid || "",
          managerName: currentProject.managerName || user?.name || "",
          // NEW: persist team lead on create
          teamLeadId: currentProject.teamLeadId || "",
          teamLeadName: currentProject.teamLeadName || "",
        });

        await logActivity({
          userId: user?.uid,
          userName: user?.name || "Unknown",
          companyId,
          action: "CREATE",
          module: "Project",
          details: `User ${user?.name} created project "${currentProject.name}"`,
        });

        await fetchProjects();
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving project:", error);
      alert("Failed to save project.");
    }
  };

  const handleDeleteProject = async (id) => {
    if (!id) {
      alert("Invalid project ID.");
      return;
    }
    try {
      await deleteDoc(doc(db, "projects", id));
      await logActivity({
        userId: user?.uid,
        userName: user?.name || "Unknown",
        companyId,
        action: "DELETE",
        module: "Project",
        details: `User ${user?.name} deleted project with ID: ${id}`,
        details: `User ${user?.name} Deleted project "${pendingDelete?.name || id}" (id: ${id}).`,
      });
      await fetchProjects();
      alert("Project deleted successfully.");
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project.");
    }
  };

  // ---------- Filtering ----------
  const filteredProjects = projects.filter((project) => {
    const matchesText = project.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesProject =
      filterProjects.length === 0 || filterProjects.includes(project.name);

    const matchesEntity =
      filterEntities.length === 0 || filterEntities.includes(project.entity);
    const projectType = entityMap[project.entity] || "";
    const matchesEntityType =
      filterEntityTypes.length === 0 || filterEntityTypes.includes(projectType);

    const matchesStatus =
      filterStatus.length === 0 || filterStatus.includes(project.status);
    const matchesPriority =
      filterPriority.length === 0 || filterPriority.includes(project.priority);
    const matchesStart =
      !startDate || new Date(project.dueDate) >= new Date(startDate);
    const matchesEnd =
      !endDate || new Date(project.dueDate) <= new Date(endDate);
    return (
      matchesProject &&
      matchesText &&
      matchesEntity &&
      matchesEntityType &&
      matchesStatus &&
      matchesPriority &&
      matchesStart &&
      matchesEnd
    );
  });

  // ---------- Sorting ----------
  const cmp = (a, b) => {
    const va = a ?? "";
    const vb = b ?? "";
    // date sort for dueDate/createdOn/modifiedOn
    if (
      sortField === "dueDate" ||
      sortField === "createdOn" ||
      sortField === "modifiedOn"
    ) {
      const da = new Date(va);
      const dbb = new Date(vb);
      return da - dbb;
    }
    return String(va).localeCompare(String(vb), undefined, {
      sensitivity: "base",
    });
  };

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const res = cmp(a[sortField], b[sortField]);
    return sortOrder === "asc" ? res : -res;
    // Note: teamLeadName sorts as a string automatically with cmp()
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // ---------- Pagination ----------
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const pagedProjects = sortedProjects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


// ---------------- CSV Helpers ----------------
const normalizeHeader = (h = "") =>
  h.toString().trim().toLowerCase().replace(/\s+/g, "");

const parseCsvText = (text) => {
  // Basic CSV parser supporting quoted cells + commas inside quotes.
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (ch === '"' && next === '"') {
      cell += '"'; // escaped quote
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }
    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i++; // handle CRLF
      row.push(cell);
      cell = "";
      // push non-empty rows
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
      continue;
    }
    cell += ch;
  }
  // last cell
  row.push(cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);

  if (!rows.length) return { headers: [], records: [] };

  const headers = rows[0].map((h) => h.trim());
  const records = rows.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, idx) => (obj[h] = (r[idx] ?? "").trim()));
    return obj;
  });
  return { headers, records };
};

const toIsoDate = (s) => {
  // Accept: yyyy-mm-dd OR dd-mm-yyyy OR dd/mm/yyyy
  if (!s) return "";
  const v = s.trim();

  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

  // dd-mm-yyyy or dd/mm/yyyy
  const m = v.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    const dd = String(m[1]).padStart(2, "0");
    const mm = String(m[2]).padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  }

  return ""; // invalid
};

const findUserByNameOrEmail = (value, list) => {
  const v = (value || "").trim().toLowerCase();
  if (!v) return null;

  // Try exact email match
  const byEmail = list.find((u) => (u.email || "").toLowerCase() === v);
  if (byEmail) return byEmail;

  // Try name match
  const byName = list.find((u) => (u.name || "").toLowerCase() === v);
  if (byName) return byName;

  // Try partial name contains (last resort)
  const contains = list.find((u) => (u.name || "").toLowerCase().includes(v));
  return contains || null;
};

// ---------------- CSV Upload ----------------
const handleUploadProjectsCsv = async (file) => {
  if (!companyId) {
    alert("Company not found. Please login again.");
    return;
  }
  if (!file) return;

  const text = await file.text();
  const { headers, records } = parseCsvText(text);

  if (!headers.length) {
    alert("CSV is empty or unreadable.");
    return;
  }

  // Expected headers (normalized)
  const expected = [
    "projectname",
    "category",
    "entity",
    "status",
    "priority",
    "projectmanagedby",
    "teamleader",
    "duedate",
  ];

  const headerMap = {};
  headers.forEach((h) => (headerMap[normalizeHeader(h)] = h));

  const missing = expected.filter((h) => !headerMap[h]);
  if (missing.length) {
    alert(
      `CSV headers missing: ${missing.join(
        ", "
      )}\n\nExpected headers:\nprojectName,category,entity,status,priority,projectManagedBy,teamLeader,dueDate`
    );
    return;
  }

  // Build fast lookups
  const entitySet = new Set(entities.map((e) => (e || "").trim()));
  const statusSet = new Set(statusOptions);
  const prioritySet = new Set(priorityOptions);

  // usersInCompany currently normalized in your code as {uid, name, role}
  // It may NOT include email; handle name match primarily.
  const allUsers = usersInCompany || [];
  const managerCandidates = managers || [];

  const errors = [];
  const validDocs = [];

  records.forEach((raw, idx) => {
    const rowNo = idx + 2; // 1 header row + 1-based line index

    const projectName = raw[headerMap["projectname"]]?.trim();
    const category = raw[headerMap["category"]]?.trim() || "";
    const entity = raw[headerMap["entity"]]?.trim();
    const status = raw[headerMap["status"]]?.trim();
    const priority = raw[headerMap["priority"]]?.trim();
    const managedBy = raw[headerMap["projectmanagedby"]]?.trim();
    const teamLeader = raw[headerMap["teamleader"]]?.trim();
    const dueDateRaw = raw[headerMap["duedate"]]?.trim();
    const dueDate = toIsoDate(dueDateRaw);

    // Required (same as handleSaveProject)
    const rowErr = [];
    if (!projectName) rowErr.push("projectName is blank");
    if (!entity) rowErr.push("entity is blank");
    if (!status) rowErr.push("status is blank");
    if (!priority) rowErr.push("priority is blank");
    if (!dueDate) rowErr.push(`dueDate invalid (${dueDateRaw || "blank"})`);

    if (entity && !entitySet.has(entity)) rowErr.push(`entity not found: "${entity}"`);
    if (status && !statusSet.has(status)) rowErr.push(`invalid status: "${status}"`);
    if (priority && !prioritySet.has(priority))
      rowErr.push(`invalid priority: "${priority}"`);

    // Resolve manager (if blank: fallback to current user)
    let managerId = user?.uid || "";
    let managerName = user?.name || "Unknown";

    if (managedBy) {
      // Prefer manager list (Manager/Admin)
      const mgr =
        findUserByNameOrEmail(managedBy, managerCandidates) ||
        findUserByNameOrEmail(managedBy, allUsers);
      if (!mgr) rowErr.push(`Project Managed By not found: "${managedBy}"`);
      else {
        managerId = mgr.uid || mgr.id || "";
        managerName = mgr.name || managedBy;
      }
    }

    // Resolve Team Leader (optional)
    let teamLeadId = "";
    let teamLeadName = "";
    if (teamLeader) {
      const tl = findUserByNameOrEmail(teamLeader, allUsers);
      if (!tl) rowErr.push(`Team Leader not found: "${teamLeader}"`);
      else {
        teamLeadId = tl.uid || tl.id || "";
        teamLeadName = tl.name || teamLeader;
      }
    }

    if (rowErr.length) {
      errors.push({ rowNo, errors: rowErr, projectName: projectName || "—" });
      return;
    }

    validDocs.push({
      name: projectName,
      category,
      entity,
      status,
      priority,
      dueDate, // ISO yyyy-mm-dd
      companyId,
      createdBy: user?.name || "Unknown",
      createdOn: new Date().toISOString(),
      modifiedOn: "",
      managerId,
      managerName,
      teamLeadId,
      teamLeadName,
    });
  });

  if (!validDocs.length) {
    alert(
      `No valid rows found.\n\nFirst few errors:\n` +
        errors
          .slice(0, 8)
          .map((e) => `Row ${e.rowNo}: ${e.errors.join("; ")}`)
          .join("\n")
    );
    return;
  }

  // Write in batches (Firestore limit is 500 ops per batch)
  try {
    const colRef = collection(db, "projects");
    let inserted = 0;

    for (let i = 0; i < validDocs.length; i += 400) {
      const chunk = validDocs.slice(i, i + 400);
      const batch = writeBatch(db);
      chunk.forEach((docData) => {
        const newRef = doc(colRef); // auto-id
        batch.set(newRef, docData);
      });
      await batch.commit();
      inserted += chunk.length;
    }

    await logActivity({
      userId: user?.uid,
      userName: user?.name || "Unknown",
      companyId,
      action: "UPLOAD",
      module: "ProjectCSV",
      details: `Uploaded CSV "${file.name}". Inserted ${inserted}. Skipped ${errors.length}.`,
    });

    await fetchProjects();

    // Summary
    if (errors.length) {
      alert(
        `Projects uploaded with warnings.\nInserted: ${inserted}\nSkipped: ${errors.length}\n\nFirst few issues:\n` +
          errors
            .slice(0, 10)
            .map((e) => `Row ${e.rowNo} (${e.projectName}): ${e.errors.join("; ")}`)
            .join("\n")
      );
    } else {
      alert(`Projects uploaded successfully.\nInserted: ${inserted}`);
    }
  } catch (e) {
    console.error("CSV upload failed:", e);
    alert("CSV upload failed. Check console logs for details.");
  }
};



  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Projects
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add Project
        </Button>

        <Button variant="contained" component="label">
          Upload CSV
          <input
            type="file"
            accept=".csv"
            hidden
            
onChange={async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  await logActivity({
    userId: user?.uid,
    userName: user?.name || "Unknown",
    companyId,
    action: "UPLOAD",
    module: "ProjectCSV",
    details: `Selected CSV "${file.name}" (${file.size} bytes).`,
  });

  await handleUploadProjectsCsv(file);

  // reset file input so same file can be re-selected
  e.target.value = "";
}}

          />
        </Button>
      </Box>

      {/* Filters — single row, no horizontal scroll */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            alignItems: "center",
            gap: 1,
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(7, minmax(0, 1fr))",
            },
          }}
        >
          {/* Project Name (multi-select) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
                multiple
                disableCloseOnSelect
                options={projectOpts}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={projectOpts.filter((o) => filterProjects.includes(o.id))}
                
              // Controlled input so typing doesn't get wiped by re-renders
                  inputValue={projectInputValue}
                  onInputChange={(_, val, reason) => {
                    // MUI fires "reset" whenever value changes; we don't want to wipe the query in that case.
                    if (reason === "input") setProjectInputValue(val || "");
                    if (reason === "clear") setProjectInputValue("");
                    // ignore "reset"
                  }}

                // NEW: add a synthetic "Select All (visible …)" row at the top
  filterOptions={(options, state) => {
   // remember the live query used by the popup
   projectInputRef.current = state.inputValue || "";
   const base = options.filter((o) => o.id !== "__ALL__");
   const filtered = projectFilter(base, state); // ← preserves search
   const visibleCount = filtered.length;
   const allVisibleOpt = {
     id: "__ALL_VISIBLE__",
     label: `Select All (visible ${visibleCount})`,
   };
   return [allVisibleOpt, ...filtered];
 }}

                onChange={(_, val, _r, details) => {
                  const clicked = details?.option;

                  // Handle "Select all (visible)"
                  if (clicked?.id === "__ALL_VISIBLE__") {
                 // visible = whatever the list shows for the *current* query
                const base = projectOpts.filter((o) => o.id !== "__ALL__" && o.id !== "__ALL_VISIBLE__");
  const visibleNow = projectFilter(base, {
    inputValue: projectInputRef.current,      // ← same text the popup is using
    getOptionLabel: (o) => o.label,
  }).map((o) => o.id);

                  const allVisibleSelected = visibleNow.length > 0 && visibleNow.every((id) => filterProjects.includes(id));
                  setFilterProjects(
                    allVisibleSelected
                      // deselect visible if they’re all selected
                      ? filterProjects.filter((id) => !visibleNow.includes(id))
                      // otherwise add all visible (preserving already-selected)
                      : Array.from(new Set([...filterProjects, ...visibleNow]))
                  );
                  return;
                  }

                  // Handle static "Select all (all projects)"
                  if (clicked?.id === "__ALL__") {
                    setFilterProjects(allProjectsSelected ? [] : projectOptions);
                    return;
                  }

                  // Regular multi-select behavior (strip special rows)
                  setFilterProjects(val.filter((v) => v.id !== "__ALL__" && v.id !== "__ALL_VISIBLE__").map((v) => v.id));
                }}
                renderOption={(props, option) => {
                  const isAll = option.id === "__ALL__";
                  const isAllVisible = option.id === "__ALL_VISIBLE__";

                  let checked = false;
                  let indeterminate = false;

                  if (isAll) {
                    checked = allProjectsSelected;
                    indeterminate = filterProjects.length > 0 && !allProjectsSelected;
                  } else if (isAllVisible) {
                    // compute "visible" set based on current search text
                     const base = projectOpts.filter((o) => o.id !== "__ALL__" && o.id !== "__ALL_VISIBLE__");
                     const visibleNow = projectFilter(base, {
                       inputValue: projectInputRef.current,
                       getOptionLabel: (o) => o.label,
                     }).map((o) => o.id);

                    const selectedVisible = visibleNow.filter((id) => filterProjects.includes(id)).length;
                    checked = visibleNow.length > 0 && selectedVisible === visibleNow.length;
                    indeterminate = selectedVisible > 0 && selectedVisible < visibleNow.length;
                  } else {
                    checked = filterProjects.includes(option.id);
                  }

                  return (
                    <li {...props}>
                      <Checkbox size="small" checked={checked} indeterminate={indeterminate} sx={{ mr: 1 }} />
                      {option.label}
                    </li>
                  );
                }}
                renderTags={() => null}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={
                      filterProjects.length ? `${filterProjects.length} selected` : "Projects"
                    }
                  />
                )}
              />
          </Box>

          {/* Entity (multi-select) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={entityOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={entityOptions.filter((o) => filterEntities.includes(o.id))}
              onChange={(_, val, _r, details) => {
                const clicked = details?.option;
                if (clicked?.id === "__ALL__") {
                  setFilterEntities(allEntitiesSelected ? [] : entities);
                  return;
                }
                setFilterEntities(
                  val.filter((v) => v.id !== "__ALL__").map((v) => v.id)
                );
              }}
              renderOption={(props, option) => {
                const isAll = option.id === "__ALL__";
                const checked = isAll
                  ? allEntitiesSelected
                  : filterEntities.includes(option.id);
                const indeterminate =
                  isAll && filterEntities.length > 0 && !allEntitiesSelected;
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </li>
                );
              }}
              renderTags={() => null}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    filterEntities.length
                      ? `${filterEntities.length} selected`
                      : "Entity"
                  }
                />
              )}
            />
          </Box>

          {/* Type (Entity Type multi-select) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={entityTypeOpts}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={entityTypeOpts.filter((o) => filterEntityTypes.includes(o.id))}
              onChange={(_, val, _r, details) => {
                const clicked = details?.option;
                if (clicked?.id === "__ALL__") {
                  setFilterEntityTypes(
                    allEntityTypesSelected ? [] : entityTypeOptions
                  );
                  return;
                }
                setFilterEntityTypes(
                  val.filter((v) => v.id !== "__ALL__").map((v) => v.id)
                );
              }}
              renderOption={(props, option) => {
                const isAll = option.id === "__ALL__";
                const checked = isAll
                  ? allEntityTypesSelected
                  : filterEntityTypes.includes(option.id);
                const indeterminate =
                  isAll && filterEntityTypes.length > 0 && !allEntityTypesSelected;
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </li>
                );
              }}
              renderTags={() => null}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    filterEntityTypes.length
                      ? `${filterEntityTypes.length} selected`
                      : "Type"
                  }
                />
              )}
            />
          </Box>

          {/* Status (multi-select) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={statusOpts}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={statusOpts.filter((o) => filterStatus.includes(o.id))}
              onChange={(_, val, _r, details) => {
                const clicked = details?.option;
                if (clicked?.id === "__ALL__") {
                  setFilterStatus(allStatusSelected ? [] : statusOptions);
                  return;
                }
                setFilterStatus(
                  val.filter((v) => v.id !== "__ALL__").map((v) => v.id)
                );
              }}
              renderOption={(props, option) => {
                const isAll = option.id === "__ALL__";
                const checked = isAll
                  ? allStatusSelected
                  : filterStatus.includes(option.id);
                const indeterminate =
                  isAll && filterStatus.length > 0 && !allStatusSelected;
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </li>
                );
              }}
              renderTags={() => null}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    filterStatus.length
                      ? `${filterStatus.length} selected`
                      : "Status"
                  }
                />
              )}
            />
          </Box>

          {/* Priority (multi-select) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={priorityOpts}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={priorityOpts.filter((o) => filterPriority.includes(o.id))}
              onChange={(_, val, _r, details) => {
                const clicked = details?.option;
                if (clicked?.id === "__ALL__") {
                  setFilterPriority(allPrioritySelected ? [] : priorityOptions);
                  return;
                }
                setFilterPriority(
                  val.filter((v) => v.id !== "__ALL__").map((v) => v.id)
                );
              }}
              renderOption={(props, option) => {
                const isAll = option.id === "__ALL__";
                const checked = isAll
                  ? allPrioritySelected
                  : filterPriority.includes(option.id);
                const indeterminate =
                  isAll && filterPriority.length > 0 && !allPrioritySelected;
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </li>
                );
              }}
              renderTags={() => null}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    filterPriority.length
                      ? `${filterPriority.length} selected`
                      : "Priority"
                  }
                />
              )}
            />
          </Box>

          {/* Start Date */}
          <Box sx={{ minWidth: 0 }}>
            <TextField
              size="small"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Start Date"
              inputProps={{ max: endDate }}
            />
          </Box>

          {/* End Date */}
          <Box sx={{ minWidth: 0 }}>
            <TextField
              size="small"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="End Date"
              inputProps={{ min: startDate }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ mt: 2, p: 2, width: "100%" }}>
        <Table
          size="small"
          sx={{
            width: "100%",
            "& th, & td": { py: 0.5 },
          }}
        >
          <TableHead sx={{ "& th": { py: 0.7 } }}>
  <TableRow>
    <TableCell sx={{ width: 80 }}>
      <TableSortLabel
        active={sortField === "srno"}
        direction={sortField === "srno" ? sortOrder : "asc"}
        onClick={() => handleSort("srno")}
      >
        Sr. No.
      </TableSortLabel>
    </TableCell>

    {/* Category (NEW) */}
    <TableCell>
      <TableSortLabel
        active={sortField === "category"}
        direction={sortField === "category" ? sortOrder : "asc"}
        onClick={() => handleSort("category")}
      >
        Category
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "name"}
        direction={sortField === "name" ? sortOrder : "asc"}
        onClick={() => handleSort("name")}
      >
        Project Name
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "entity"}
        direction={sortField === "entity" ? sortOrder : "asc"}
        onClick={() => handleSort("entity")}
      >
        Entity
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "status"}
        direction={sortField === "status" ? sortOrder : "asc"}
        onClick={() => handleSort("status")}
      >
        Status
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "priority"}
        direction={sortField === "priority" ? sortOrder : "asc"}
        onClick={() => handleSort("priority")}
      >
        Priority
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "dueDate"}
        direction={sortField === "dueDate" ? sortOrder : "asc"}
        onClick={() => handleSort("dueDate")}
      >
        Due Date
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "createdOn"}
        direction={sortField === "createdOn" ? sortOrder : "asc"}
        onClick={() => handleSort("createdOn")}
      >
        Created On
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "modifiedOn"}
        direction={sortField === "modifiedOn" ? sortOrder : "asc"}
        onClick={() => handleSort("modifiedOn")}
      >
        Modified On
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "managerName"}
        direction={sortField === "managerName" ? sortOrder : "asc"}
        onClick={() => handleSort("managerName")}
      >
        Managed By
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "teamLeadName"}
        direction={sortField === "teamLeadName" ? sortOrder : "asc"}
        onClick={() => handleSort("teamLeadName")}
      >
        Team Leader
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={sortField === "createdBy"}
        direction={sortField === "createdBy" ? sortOrder : "asc"}
        onClick={() => handleSort("createdBy")}
      >
        Created By
      </TableSortLabel>
    </TableCell>

    <TableCell>Actions</TableCell>
  </TableRow>
</TableHead>


          <TableBody>
            {pagedProjects.map((project, i) => (
              <TableRow
                key={project.id}
                hover
                sx={{
                  "& td": { py: 0.5 },
                  "& .MuiIconButton-root": { p: 0.5 },
                }}
              >
                <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                <TableCell>{project.category || "—"}</TableCell>
                <TableCell>{project.name}</TableCell>
                <TableCell>{project.entity}</TableCell>
                <TableCell>{project.status}</TableCell>
                <TableCell>{project.priority}</TableCell>
                <TableCell>{formatDate(project.dueDate)}</TableCell>
                <TableCell>{formatDate(project.createdOn)}</TableCell>
                <TableCell>{formatDate(project.modifiedOn)}</TableCell>
                <TableCell>{project.managerName || "—"}</TableCell>
                {/* NEW: show Team Leader */}
                <TableCell>{project.teamLeadName || "—"}</TableCell>
                <TableCell>{project.createdBy || "—"}</TableCell>

                <TableCell sx={{ minWidth: 90 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenDialog(project)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRequestDeleteProject(project)}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {pagedProjects.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No projects to show.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={sortedProjects.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Dialog for Adding/Editing Projects */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{currentProject?.id ? "Edit Project" : "Add Project"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Project Name"
            fullWidth
            sx={{ mt: 2 }}
            value={currentProject.name}
            onChange={(e) => setCurrentProject({ ...currentProject, name: e.target.value })}
          />

          
        {/* NEW: Category (free text or pick from list). Typing a new value auto-adds it to options */}
          <Autocomplete
            freeSolo
            options={categoryOptions}
            value={currentProject.category || ""}
            onChange={(_, val) => {
              const v = (val || "").trim();
              if (v && !categoryOptions.includes(v)) {
                setCategoryOptions((prev) => [...prev, v].sort((a, b) => a.localeCompare(b)));

     logActivity({
       userId: user?.uid,
       userName: user?.name || "Unknown",
       companyId,
       action: "CREATE",
       module: "ProjectCategory",
       details: `Added new project category "${v}".`,
     });


              }
              setCurrentProject((p) => ({ ...p, category: v }));
            }}
            onInputChange={(_, val) => {
              // keep currentProject.category in sync while typing
              setCurrentProject((p) => ({ ...p, category: (val || "").trim() }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Add or Select Category " sx={{ mt: 2 }} placeholder="e.g., Audit & Assurance" />
            )}
          />

          <TextField
  select
  label="Entity"
  fullWidth
  sx={{ mt: 2 }}
  value={currentProject.entity}
  onChange={(e) => setCurrentProject({ ...currentProject, entity: e.target.value })}
  SelectProps={{
    onOpen: () => setEntityQuery(""),
    MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
  }}
>
  <ListSubheader
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 1,
      bgcolor: "background.paper",
      p: 1,
    }}
  >
    <TextField
      autoFocus
      size="small"
      placeholder="Search entities..."
      value={entityQuery}
      onChange={(e) => setEntityQuery(e.target.value)}
      onKeyDown={(e) => e.stopPropagation()}
      fullWidth
    />
  </ListSubheader>

  {filteredEntityNames.length ? (
    filteredEntityNames.map((entity) => (
      <MenuItem key={entity} value={entity}>
        {entity}
      </MenuItem>
    ))
  ) : (
    <MenuItem disabled>No matches</MenuItem>
  )}

  {/* Keeps existing selected value visible even if it doesn't match current search */}
  {currentProject.entity &&
    !filteredEntityNames.some(
      (n) => n.toLowerCase() === currentProject.entity.trim().toLowerCase()
    ) && (
      <MenuItem value={currentProject.entity}>{currentProject.entity}</MenuItem>
    )}
</TextField>

          <TextField
            select
            label="Status"
            fullWidth
            sx={{ mt: 2 }}
            value={currentProject.status}
            onChange={(e) => setCurrentProject({ ...currentProject, status: e.target.value })}
          >
            {statusOptions.map((status, index) => (
              <MenuItem key={index} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Priority"
            fullWidth
            sx={{ mt: 2 }}
            value={currentProject.priority}
            onChange={(e) => setCurrentProject({ ...currentProject, priority: e.target.value })}
          >
            {priorityOptions.map((priority, index) => (
              <MenuItem key={index} value={priority}>
                {priority}
              </MenuItem>
            ))}
          </TextField>

          {/* Project Managed By */}
          <TextField
  select
  label="Project Managed By"
  fullWidth
  sx={{ mt: 2 }}
  value={currentProject.managedById || ""}
  onChange={(e) => {
    const sel = usersInCompany.find((u) => u.uid === e.target.value);
    setCurrentProject((p) => ({
      ...p,
      managedById: sel?.uid || "",
      managedByName: sel?.name || "",
    }));
  }}
  SelectProps={{
    onOpen: () => setManagedByQuery(""),
    MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
  }}
>
  <ListSubheader
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 1,
      bgcolor: "background.paper",
      p: 1,
    }}
  >
    <TextField
      autoFocus
      size="small"
      placeholder="Search users..."
      value={managedByQuery}
      onChange={(e) => setManagedByQuery(e.target.value)}
      onKeyDown={(e) => e.stopPropagation()}
      fullWidth
    />
  </ListSubheader>

  <MenuItem value="">— None —</MenuItem>

  {filteredManagedByUsers.length ? (
    filteredManagedByUsers.map((u) => (
      <MenuItem key={u.uid} value={u.uid}>
        {u.name}
      </MenuItem>
    ))
  ) : (
    <MenuItem disabled>No matches</MenuItem>
  )}
</TextField>


          {/* NEW: Team Leader */}
          <TextField
  select
  label="Team Leader"
  fullWidth
  sx={{ mt: 2 }}
  value={currentProject.teamLeadId || ""}
  onChange={(e) => {
    const sel = usersInCompany.find((u) => u.uid === e.target.value);
    setCurrentProject((p) => ({
      ...p,
      teamLeadId: sel?.uid || "",
      teamLeadName: sel?.name || "",
    }));
  }}
  SelectProps={{
    onOpen: () => setTeamLeadQuery(""),
    MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
  }}
>
  <ListSubheader
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 1,
      bgcolor: "background.paper",
      p: 1,
    }}
  >
    <TextField
      autoFocus
      size="small"
      placeholder="Search team leaders..."
      value={teamLeadQuery}
      onChange={(e) => setTeamLeadQuery(e.target.value)}
      onKeyDown={(e) => e.stopPropagation()}
      fullWidth
    />
  </ListSubheader>

  <MenuItem value="">— None —</MenuItem>

  {filteredTeamLeadUsers.length ? (
    filteredTeamLeadUsers.map((u) => (
      <MenuItem key={u.uid} value={u.uid}>
        {u.name}
      </MenuItem>
    ))
  ) : (
    <MenuItem disabled>No matches</MenuItem>
  )}
</TextField>



          <TextField
            label="Due Date"
            type="date"
            fullWidth
            sx={{ mt: 2 }}
            value={currentProject.dueDate}
            onChange={(e) => setCurrentProject({ ...currentProject, dueDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProject}>
            {currentProject?.id ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete project?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" color="text.secondary">
            {pendingDelete ? (
              <>This will permanently remove <strong>{pendingDelete.name}</strong>.</>
            ) : (
              "This will permanently remove the project."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteProject}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Projects;
